import { useState, useEffect, useMemo } from "react";
import "./App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Play, History, Home, User, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// --- HOOKS ---
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return isMobile;
};

// --- COMPONENTS ---
const NumberInput = ({ value, onChange, min = 0, max, isMobile, disabled = false }) => {
    const stringValue = String(value);

    const handleChange = (val) => {
        const num = parseInt(val, 10);
        const newValue = isNaN(num) ? 0 : num;
        if (newValue >= min && (max === undefined || newValue <= max)) {
            onChange(newValue);
        }
    };

    if (isMobile) {
        const options = Array.from({ length: (max ?? 0) - min + 1 }, (_, i) => min + i);
        return (
            <Select value={stringValue} onValueChange={handleChange} disabled={disabled}>
                <SelectTrigger className="w-20 mx-auto" disabled={disabled}>
                    <SelectValue>{stringValue}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {options.map(num => <SelectItem key={num} value={String(num)}>{num}</SelectItem>)}
                </SelectContent>
            </Select>
        );
    }
    return <Input type="number" min={min} max={max} value={stringValue} onChange={(e) => handleChange(e.target.value)} className="text-center w-20 mx-auto" disabled={disabled} />;
};


// --- HELPERS ---
const calculatePoints = (bet, wins, multiplier = 1) => {
  if (bet === wins) return (bet === 0 ? 10 : 10 + 5 * bet) * multiplier;
  return -5 * Math.abs(wins - bet) * multiplier;
};

const calculateNextRound = (game) => {
  const numPlayers = game.players.length;
  if (numPlayers === 0) return { cards: 1, phase: "subida", isBlind: true };
  const maxCards = Math.floor(40 / numPlayers);
  if (game.current_round === 0) return { cards: 1, phase: "subida", isBlind: true };

  const lastRound = game.rounds[game.rounds.length - 1];
  if (!lastRound) return { cards: 1, phase: "subida", isBlind: true };

  const { cards_per_player: lastCards, phase: lastPhase } = lastRound;
  const completaCount = game.rounds.filter(r => r.phase === "completa").length;
  const subastaCount = game.rounds.filter(r => r.phase === "subasta").length;

  if (lastPhase === "subida") {
      const nextCards = lastCards + 1;
      // Si la siguiente ronda YA tiene el número máximo de cartas, su fase DEBE ser "completa".
      if (nextCards === maxCards) {
          return { cards: maxCards, phase: "completa", isBlind: false };
      }
      // Si no, seguimos en subida.
      if (nextCards < maxCards) {
          return { cards: nextCards, phase: "subida", isBlind: false };
      }
      // Como red de seguridad, si por algún motivo llegamos a una ronda de "subida" con maxCards, la siguiente será "completa".
      return { cards: maxCards, phase: "completa", isBlind: false };
  }

  if (lastPhase === "completa") return completaCount < numPlayers ? { cards: maxCards, phase: "completa", isBlind: false } : { cards: maxCards - 1, phase: "bajada", isBlind: false };
  if (lastPhase === "bajada" && lastCards > 1) return { cards: lastCards - 1, phase: "bajada", isBlind: lastCards - 1 === 1 };
  if (lastPhase === "bajada") return { cards: maxCards, phase: "subasta", isBlind: false };
  if (lastPhase === "subasta" && subastaCount < numPlayers) return { cards: maxCards, phase: "subasta", isBlind: false };
  
  return { cards: 1, phase: "subida", isBlind: false }; // Fin de la partida o caso por defecto
};



function App() {
  const [currentView, setCurrentView] = useState("home");
  const [players, setPlayers] = useState(["", ""]);
  const [currentGame, setCurrentGame] = useState(null);
  const [games, setGames] = useState([]);
  const [cardsPerPlayer, setCardsPerPlayer] = useState(1);
  const [phase, setPhase] = useState("subida");
  const [isBlind, setIsBlind] = useState(false);
  const [trump, setTrump] = useState("copas");
  const [multiplier, setMultiplier] = useState(1);
  const [roundData, setRoundData] = useState({});
  const [showRoundConfig, setShowRoundConfig] = useState(true);
  const [handPlayer, setHandPlayer] = useState(0);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const isMobile = useIsMobile();

  const initializeRoundData = (players) => {
    return players.reduce((acc, p) => ({ ...acc, [p]: { bet: 0, wins: 0 } }), {});
  };

  useEffect(() => {
    loadGames();
    const urlParams = new URLSearchParams(window.location.search);
    const viewGameId = urlParams.get('view');
    if (viewGameId) {
      loadAndSelectGame(viewGameId, "view");
    }
  }, []);

  useEffect(() => { if (currentView === "history") loadGames(); }, [currentView]);

  useEffect(() => {
    let interval;
    if (isViewMode && currentGame && autoRefresh) {
      interval = setInterval(() => {
        axios.get(`${API}/games/${currentGame.id}`)
          .then(res => setCurrentGame(res.data))
          .catch(err => console.error("Error refreshing game:", err));
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isViewMode, currentGame, autoRefresh]);

  const validation = useMemo(() => {
    if (!currentGame || showRoundConfig) return { isValid: false, errors: [], canFinish: false };
    
    let errors = [];
    let totalBets = 0;
    let totalWins = 0;
    
    currentGame.players.forEach(p => {
      const data = roundData[p] || { bet: 0, wins: 0 };
      totalBets += data.bet;
      totalWins += data.wins;
    });
    
    const lastRound = currentGame.rounds[currentGame.rounds.length - 1];
    if (!lastRound) return { isValid: false, errors: [], canFinish: false };
    
    const currentCards = lastRound.cards_per_player;
    if (totalBets === currentCards) {
        errors.push(`La suma de apuestas (${totalBets}) no puede ser igual a las cartas (${currentCards})`);
    }
    if (totalWins !== currentCards) {
        errors.push(`La suma de bazas ganadas (${totalWins}) debe ser igual a las cartas (${currentCards})`);
    }
    
    const canFinish = errors.length === 0;
    return { isValid: canFinish, errors, canFinish };
  }, [roundData, currentGame, showRoundConfig]);

  const loadGames = () => axios.get(`${API}/games`).then(res => setGames(res.data)).catch(() => toast.error("Error al cargar partidas"));

  const loadAndSelectGame = (gameId, mode = "edit") => {
    axios.get(`${API}/games/${gameId}`).then(response => {
      const game = response.data;
      setCurrentGame(game);
      setRoundData(initializeRoundData(game.players));
      setIsViewMode(mode === "view");
      setAutoRefresh(mode === "view");
      const nextRound = calculateNextRound(game);
      setCardsPerPlayer(nextRound.cards);
      setPhase(nextRound.phase);
      setIsBlind(nextRound.isBlind);
      setTrump("copas");
      setMultiplier(1);
      setHandPlayer(game.current_round % game.players.length);
      const lastRound = game.rounds[game.rounds.length - 1];
      const isRoundScored = lastRound?.scores && lastRound.scores.length > 0;
      if (mode === 'view') {
        setShowRoundConfig(false);
      } else {
        setShowRoundConfig(game.rounds.length === 0 || isRoundScored);
      }
      setCurrentView("game");
    }).catch(() => toast.error("Error al cargar la partida"));
  };

  const addPlayer = () => setPlayers([...players, ""]);
  const removePlayer = (index) => setPlayers(players.filter((_, i) => i !== index));
  const updatePlayer = (index, value) => setPlayers(players.map((p, i) => i === index ? value : p));

  const updateRoundData = (player, field, value) => {
    setRoundData(prev => ({
        ...prev,
        [player]: {
            ...prev[player],
            [field]: value,
        },
    }));
  };

  const startGame = () => {
    const validPlayers = players.filter(p => p.trim() !== "");
    if (validPlayers.length < 2) return toast.error("Se necesitan al menos 2 jugadores");
    axios.post(`${API}/games`, { players: validPlayers }).then(res => {
      loadAndSelectGame(res.data.id, "edit");
      toast.success("¡Partida creada!");
    }).catch(() => toast.error("Error al crear partida"));
  };

  const startNewRound = () => {
    if (!currentGame) return;
    axios.post(`${API}/games/${currentGame.id}/round/start`, { cards_per_player: cardsPerPlayer, phase, is_blind: isBlind, trump, multiplier })
      .then(() => axios.get(`${API}/games/${currentGame.id}`))
      .then(res => {
        setCurrentGame(res.data);
        setRoundData(initializeRoundData(res.data.players));
        setShowRoundConfig(false);
        setAttemptedSubmit(false);
        toast.success("Nueva ronda iniciada");
      }).catch(() => toast.error("Error al iniciar ronda"));
  };

  const finishRound = async () => {
    setAttemptedSubmit(true);
    if (!currentGame || !validation.canFinish) return toast.error("Por favor completa todos los datos correctamente.");
    try {
      await axios.post(`${API}/games/${currentGame.id}/round/bets`, { bets: Object.entries(roundData).map(([p, d]) => ({ player: p, bet: d.bet })) });
      await axios.post(`${API}/games/${currentGame.id}/round/wins`, { wins: Object.entries(roundData).map(([p, d]) => ({ player: p, wins: d.wins })) });
      await axios.get(`${API}/games/${currentGame.id}/round/calculate`);
      const res = await axios.get(`${API}/games/${currentGame.id}`);
      setCurrentGame(res.data);
      toast.success("¡Ronda finalizada!");
      const nextRound = calculateNextRound(res.data);
      setCardsPerPlayer(nextRound.cards);
      setPhase(nextRound.phase);
      setIsBlind(nextRound.isBlind);
      setTrump("copas");
      setMultiplier(1);
      setHandPlayer((handPlayer + 1) % res.data.players.length);
      setShowRoundConfig(true);
      setAttemptedSubmit(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al finalizar ronda");
    }
  };

  const finishGame = () => {
    if (!currentGame) return;
    axios.post(`${API}/games/${currentGame.id}/finish`).then(() => {
      toast.success("¡Partida finalizada!");
      setCurrentView("home");
      setCurrentGame(null);
      loadGames();
    }).catch(() => toast.error("Error al finalizar partida"));
  };
  
  const deleteGame = (gameId) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta partida? Esta acción no se puede deshacer.")) {
      axios.delete(`${API}/games/${gameId}`).then(() => {
        toast.success("Partida eliminada");
        if (currentGame && currentGame.id === gameId) {
          setCurrentView("home");
          setCurrentGame(null);
        }
        loadGames();
      }).catch(() => toast.error("Error al eliminar partida"));
    }
  };

  const copyShareLink = (gameId) => {
    navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?view=${gameId}`)
      .then(() => toast.success("Link copiado al portapapeles"))
      .catch(() => toast.error("Error al copiar el link"));
  };

  if (currentView === "home") {
    const activeGames = games.filter(g => g.status === "active");
    return (
      <div className="App"><div className="container">
        <Card className="welcome-card">
          <CardHeader>
            <CardTitle>Contador de Pocha</CardTitle>
            <CardDescription>Gestiona tus partidas de pocha fácilmente</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setCurrentView("setup")} size="lg" className="w-full mb-2"><Play className="mr-2 h-5 w-5" />Nueva Partida</Button>
            <Button onClick={() => setCurrentView("history")} variant="outline" size="lg" className="w-full"><History className="mr-2 h-5 w-5" />Historial</Button>
          </CardContent>
        </Card>
        {activeGames.length > 0 && (
          <Card><CardHeader><CardTitle>Partidas en Curso</CardTitle></CardHeader><CardContent className="space-y-3">
            {activeGames.map(game => (
              <div key={game.id} className="flex justify-between items-center p-3 bg-secondary rounded-lg">
                <div>
                  <p className="font-semibold">{game.players.join(", ")}</p>
                  <p className="text-sm text-muted-foreground">Ronda {game.current_round + 1}</p>
                </div>
                <Button onClick={() => loadAndSelectGame(game.id, "edit")}>Continuar</Button>
              </div>
            ))}
          </CardContent></Card>
        )}
      </div></div>
    );
  }

  if (currentView === "setup") {
    return (
      <div className="App"><div className="container">
        <Card><CardHeader><CardTitle>Configurar Partida</CardTitle><CardDescription>Añade los nombres de los jugadores</CardDescription></CardHeader><CardContent>
          <div className="space-y-3 mb-4">{players.map((player, index) => (<div key={index} className="flex items-center gap-2"><Input type="text" placeholder={`Jugador ${index + 1}`} value={player} onChange={(e) => updatePlayer(index, e.target.value)} /><Button variant="ghost" size="icon" onClick={() => removePlayer(index)} disabled={players.length <= 2}><Trash2 className="h-4 w-4" /></Button></div>))}</div>
          <Button onClick={addPlayer} variant="outline" className="w-full mb-4"><Plus className="mr-2 h-4 w-4" />Añadir Jugador</Button>
          <div className="flex gap-2"><Button onClick={() => setCurrentView("home")} variant="outline" className="w-1/3">Volver</Button><Button onClick={startGame} className="w-2/3">Comenzar Partida</Button></div>
        </CardContent></Card>
      </div></div>
    );
  }

  if (currentView === "history") {
    return (
      <div className="App"><div className="container">
        <Card><CardHeader><div className="flex justify-between items-center"><CardTitle>Historial de Partidas</CardTitle><Button onClick={() => setCurrentView("home")} variant="outline"><Home className="mr-2 h-4 w-4" />Inicio</Button></div></CardHeader><CardContent className="space-y-3">
          {games.length === 0 ? <p>No hay partidas guardadas</p> : games.map(game => (<div key={game.id} className="flex justify-between items-center p-3 bg-secondary rounded-lg"><div className="game-info"><p className="font-semibold">{game.players.join(", ")}</p><p className="text-sm text-muted-foreground">{new Date(game.created_at).toLocaleDateString()} - {game.status}</p></div><div className="flex gap-2">
            <Button onClick={() => loadAndSelectGame(game.id, "view")} size="sm" variant="outline">Ver</Button>
            {game.status === "active" && <Button onClick={() => loadAndSelectGame(game.id, "edit")} size="sm">Continuar</Button>}
            <Button onClick={() => deleteGame(game.id)} size="icon" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
          </div></div>))}
        </CardContent></Card>
      </div></div>
    );
  }

  if (currentView === "game" && currentGame) {
    const currentRoundNumber = showRoundConfig ? currentGame.current_round + 1 : currentGame.current_round;
    const lastRound = currentGame.rounds[currentGame.rounds.length - 1];
    const subastaCount = currentGame.rounds.filter(r => r.phase === "subasta").length;
    const shouldGameEnd = subastaCount >= currentGame.players.length && showRoundConfig;

    return (
      <div className="App"><div className="container">
        <div className="game-header">
          <div><h1 className="game-title">Ronda {currentRoundNumber}</h1><p className="text-sm text-muted-foreground">Jugadores: {currentGame.players.join(", ")}</p></div>
          <div className="flex gap-2">
            <Button onClick={() => copyShareLink(currentGame.id)} variant="outline" size="icon"><Share2 className="h-4 w-4" /></Button>
            <Button onClick={() => { setCurrentView("home"); setCurrentGame(null); }} variant="outline" size="icon"><Home className="h-4 w-4" /></Button>
            {!isViewMode && <Button onClick={() => deleteGame(currentGame.id)} variant="destructive" size="icon"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>      
        {!isViewMode && (
          <fieldset disabled={isViewMode}>
            <Card className={cn('mb-4 border-2', showRoundConfig ? 'border-primary' : 'border-transparent', { 'card-inactive': !showRoundConfig && !isViewMode })}>
                <CardHeader><CardTitle>Configuración de Ronda</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><label className="label">Cartas</label><Input type="number" min="1" value={cardsPerPlayer} onChange={e => setCardsPerPlayer(parseInt(e.target.value) || 1)} disabled={!showRoundConfig} /></div>
                    <div><label className="label">Fase</label><Select value={phase} onValueChange={setPhase} disabled={!showRoundConfig}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="subida">Subida</SelectItem><SelectItem value="completa">Completa</SelectItem><SelectItem value="bajada">Bajada</SelectItem><SelectItem value="subasta">Subasta</SelectItem></SelectContent></Select></div>
                    <div><label className="label">Triunfo</label><Select value={trump} onValueChange={val => { setTrump(val); setMultiplier(val === 'oros' ? 2 : (val === '5bastos' ? 3 : 1)); }} disabled={!showRoundConfig}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="oros">Oros (x2)</SelectItem><SelectItem value="copas">Copas</SelectItem><SelectItem value="espadas">Espadas</SelectItem><SelectItem value="bastos">Bastos</SelectItem><SelectItem value="5bastos">5 Bastos (x3)</SelectItem></SelectContent></Select></div>
                    <div><label className="label">Multiplicador</label><Input type="number" value={multiplier} disabled className="bg-muted" /></div>
                  </div>
                  {!shouldGameEnd && <Button onClick={startNewRound} className="w-full" disabled={!showRoundConfig}><Play className="mr-2 h-4 w-4" />Comenzar Ronda</Button>}
                  {shouldGameEnd && <Button onClick={finishGame} className="w-full" variant="destructive">Finalizar Partida</Button>}
                </CardContent>
            </Card>
          </fieldset>
        )}

{lastRound && (
          <fieldset disabled={isViewMode || showRoundConfig}>
            <Card className={cn('mb-4 border-2', !showRoundConfig ? 'border-primary' : 'border-transparent', { 'card-inactive': showRoundConfig && !isViewMode })}>
                <CardHeader>
                  <CardDescription className="label-title">Recoger Puntuación</CardDescription>
                  <CardTitle>{lastRound.cards_per_player} cartas • {lastRound.phase} {lastRound.is_blind && "🎲"}</CardTitle>
                  <CardDescription>Triunfo: {lastRound.trump} • Multiplicador: x{lastRound.multiplier}</CardDescription>
                  {!isViewMode && <div className="flex items-center gap-2 mt-2 text-sm font-medium"><User className="h-4 w-4" />Mano: {currentGame.players[handPlayer]}</div>}
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Jugador</TableHead><TableHead className="text-center">Apuesta</TableHead><TableHead className="text-center">Bazas</TableHead><TableHead className="text-right">Puntos</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {currentGame.players.map(player => {
                        if (isViewMode) {
                          const scoreData = lastRound.scores?.find(s => s.player === player);
                          const points = scoreData?.points ?? '-';
                          return (
                            <TableRow key={player}>
                              <TableCell className="font-medium">{player}</TableCell>
                              <TableCell className="text-center">{scoreData?.bet ?? '-'}</TableCell>
                              <TableCell className="text-center">{scoreData?.wins ?? '-'}</TableCell>
                              <TableCell className={`text-right font-semibold ${points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {points > 0 ? '+' : ''}{points}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        const data = roundData[player] || { bet: 0, wins: 0 };
                        const points = calculatePoints(data.bet, data.wins, lastRound.multiplier);
                        return (
                          <TableRow key={player}>
                            <TableCell className="font-medium">{player}</TableCell>
                            <TableCell><NumberInput value={data.bet} onChange={v => updateRoundData(player, 'bet', v)} min={0} max={lastRound.cards_per_player} isMobile={isMobile} /></TableCell>
                            <TableCell><NumberInput value={data.wins} onChange={v => updateRoundData(player, 'wins', v)} min={0} max={lastRound.cards_per_player} isMobile={isMobile} /></TableCell>
                            <TableCell className={`text-right font-semibold ${points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {points > 0 ? '+' : ''}{points}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {!isViewMode && (
                    <>
                      <div className="summary-info mt-4">
                        <div className="flex justify-between text-sm">
                          <span>Total apuestas:</span>
                          <span className="font-semibold">{Object.values(roundData).reduce((s, d) => s + (d.bet || 0), 0)} / {lastRound.cards_per_player}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Total bazas:</span>
                          <span className="font-semibold">{Object.values(roundData).reduce((s, d) => s + (d.wins || 0), 0)} / {lastRound.cards_per_player}</span>
                        </div>
                      </div>
                      {attemptedSubmit && validation.errors.length > 0 && <div className="validation-error mt-3 text-red-600 text-sm">{validation.errors.map((e, i) => <p key={i}>⚠️ {e}</p>)}</div>}
                      <Button onClick={finishRound} disabled={!validation.canFinish} className="w-full mt-4" size="lg">Finalizar Ronda</Button>
                    </>
                  )}
                </CardContent>
            </Card>
          </fieldset>
        )}
        
        <Card className="mb-4">
            <CardHeader>
              <CardTitle>Puntuación Total</CardTitle>
              <CardDescription>{currentGame.rounds.length} rondas jugadas</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Posición</TableHead><TableHead>Jugador</TableHead><TableHead className="text-right">Puntos</TableHead></TableRow></TableHeader>
                <TableBody>
                {Object.entries(currentGame.total_scores).sort((a,b) => b[1] - a[1]).map(([player, score], idx, arr) => (
                    <TableRow key={player} className={idx === 0 ? "font-bold bg-amber-50" : ""}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{player} {idx === 0 && "👑"} {idx === arr.length - 1 && idx !== 0 && "💩"}</TableCell>
                      <TableCell className="text-right text-lg">{score}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {currentGame.rounds.length > 0 && <div className="mt-6">
                <h3 className="font-semibold mb-3">Historial de Rondas</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                        <TableHead>Ronda</TableHead><TableHead>Fase</TableHead><TableHead>Cartas</TableHead><TableHead>Triunfo</TableHead>
                        {currentGame.players.map(p => <TableHead key={p} className="text-center">{p}</TableHead>)}
                    </TableRow></TableHeader>
                    <TableBody>
                      {currentGame.rounds.map(round => (
                        <TableRow key={round.round_number}>
                          <TableCell>{round.round_number}</TableCell>
                          <TableCell>{round.phase} {round.is_blind && "🎲"}</TableCell>
                          <TableCell>{round.cards_per_player}</TableCell>
                          <TableCell>{round.trump}{round.multiplier > 1 && ` (x${round.multiplier})`}</TableCell>
                          {currentGame.players.map(player => {
                            const score = round.scores?.find(s => s.player === player);
                            return <TableCell key={player} className={`text-center font-semibold ${score?.points == null ? '' : score.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>{score ? (score.points > 0 ? '+' : '') + score.points : '-'}</TableCell>
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>}
            </CardContent>
          </Card>
      </div></div>
    );
  }

  return null;
}

export default App;
